import {
  CurrencyDollarSimple,
  Binoculars,
  Lifebuoy,
  Quotes,
  Buildings,
} from "@phosphor-icons/react";
import * as React from "react";
import { Dropdown } from "../Dropdown";
import styles from "../Navbar.module.css";
import chromeIcon from "~/assets/Chrome-icon.svg";
import { ReadMoreLink } from "~/components/DesignSystem/ReadMoreLink";
import { useCursorFollow } from "~/components/DesignSystem/utils/useCursorFollow";

export interface ResourcesDropdownContentProps {
  isSwitching?: boolean;
  isExiting?: boolean;
  chromeExtensionUrl?: string;
}

// ROI icon - currency dollar for money/ROI
const RoiIcon = () => (
  <div className={`${styles.productIcon} ${styles.roiIcon}`}>
    <CurrencyDollarSimple size={19} weight="fill" />
  </div>
);

// Blog icon - quotes for content/testimonials
const BlogIcon = () => (
  <div className={`${styles.productIcon} ${styles.blogIcon}`}>
    <Quotes size={19} weight="fill" />
  </div>
);

// Trends Reports icon - binoculars for forecasting
const TrendsIcon = () => (
  <div className={`${styles.productIcon} ${styles.trendsIcon}`}>
    <Binoculars size={19} weight="fill" />
  </div>
);

// Resource Center icon - lifebuoy for support/resources
const ResourceCenterIcon = () => (
  <div className={`${styles.productIcon} ${styles.resourceCenterItemIcon}`}>
    <Lifebuoy size={19} weight="fill" />
  </div>
);

// Supplier Coverage icon - buildings for suppliers/vendors
const SupplierCoverageIcon = () => (
  <div className={`${styles.productIcon} ${styles.supplierCoverageIcon}`}>
    <Buildings size={19} weight="fill" />
  </div>
);

const ChromeExtensionCard: React.FC<{ chromeExtensionUrl?: string }> = ({
  chromeExtensionUrl,
}) => {
  const cardRef = React.useRef<HTMLAnchorElement>(null);
  const [isHovered, setIsHovered] = React.useState(false);
  const { handleMouseMove, handleMouseEnter, handleMouseLeave } =
    useCursorFollow({
      elementRef: cardRef,
      onHoverClass: styles.chromeCardHovering,
    });

  const onMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    handleMouseEnter(e);
  };

  const onMouseLeave = () => {
    setIsHovered(false);
    handleMouseLeave();
  };

  const href = chromeExtensionUrl || "/chrome-extension";

  return (
    <a
      ref={cardRef}
      href={href}
      target={chromeExtensionUrl ? "_blank" : undefined}
      rel={chromeExtensionUrl ? "noopener noreferrer" : undefined}
      className={styles.chromeCard}
      onMouseMove={handleMouseMove}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.chromeCardOverlay} aria-hidden="true" />
      <div className={styles.chromeCardContent}>
        <div className={styles.chromeCardText}>
          <span className={styles.chromeCardLabel}>Browser Extension</span>
          <span className={styles.chromeCardTitle}>
            Pricing intel,
            <br />
            everywhere you go
          </span>
        </div>
        <div className={styles.chromeCardVisual}>
          <img src={chromeIcon} alt="" className={styles.chromeCardIcon} />
        </div>
      </div>
      <div className={styles.chromeCardFooter}>
        <ReadMoreLink href={href} forceHover={isHovered} as="span">
          Add to Chrome
        </ReadMoreLink>
      </div>
    </a>
  );
};

export const ResourcesDropdownContent: React.FC<
  ResourcesDropdownContentProps
> = ({ isSwitching, isExiting, chromeExtensionUrl }) => (
  <Dropdown
    width="wide"
    featuredOnRight
    isSwitching={isSwitching}
    isExiting={isExiting}
    sections={[
      {
        title: "RESOURCES",
        items: [
          {
            icon: <RoiIcon />,
            title: "ROI",
            description:
              "See how Vendr delivers measurable savings and ROI for companies of all sizes",
            href: "/resources/roi",
          },
          {
            icon: <SupplierCoverageIcon />,
            title: "Supplier Coverage",
            description:
              "Discover which SaaS vendors and software suppliers are covered by Vendr",
            href: "/resources/supplier-coverage",
          },
          {
            icon: <BlogIcon />,
            title: "Blog",
            description:
              "Read the latest insights, tips, and best practices for procurement and negotiations",
            href: "/blog",
            disabled: true,
          },
        ],
      },
      {
        title: "TOOLS",
        items: [
          {
            icon: <TrendsIcon />,
            title: "Trends Reports",
            description:
              "Access comprehensive market trends and pricing reports to inform your strategy",
            href: "/resources/trends",
            disabled: true,
          },
          {
            icon: <SupplierCoverageIcon />,
            title: "Supplier Coverage",
            description:
              "Browse our coverage across top SaaS providers with savings insights",
            href: "/resources/supplier-coverage",
          },
          {
            icon: <ResourceCenterIcon />,
            title: "Resource Center",
            description: "Learn how modern, intelligent procurement works",
            href: "/resources",
            disabled: true,
          },
        ],
      },
    ]}
    featured={<ChromeExtensionCard chromeExtensionUrl={chromeExtensionUrl} />}
  />
);
